export const SCAPP_METRICS = [
  "alarms_count",
  "perdidos_count",
  "sfr_value",
  "delivered_food",
  "visitas_count",
  "alarms_by_ton",
  "alarms_by_visits",
  "fasts_count",
];

export const DERIVED_FEATURES = {
  ppa_per_visit: {
    label: "PPA por visita",
    operation: "ratio",
    numeratorMetric: "perdidos_count",
    denominatorMetric: "visitas_count",
  },
  alarms_per_ton: {
    label: "Alarmas por tonelada",
    operation: "ratio",
    numeratorMetric: "alarms_count",
    denominatorMetric: "delivered_food",
  },
  sfr_delta: {
    label: "Delta SFR",
    operation: "delta",
    sourceMetric: "sfr_value",
  },
  moving_avg_sfr: {
    label: "Promedio móvil SFR",
    operation: "movingAverage",
    sourceMetric: "sfr_value",
    window: 3,
  },
};

const ALLOWED_FAMILIES = new Set([
  "metric_by_entity",
  "metric_by_time",
  "metric_vs_metric",
  "derived_metric_by_entity",
  "derived_metric_by_time",
  "correlation",
  "ranking",
]);
const ALLOWED_CHART_TYPES = new Set(["bar", "line", "stackedBar", "dualAxis", "doughnut", "scatter"]);
const METRIC_SET = new Set(SCAPP_METRICS);

export function validateInsightChartSpec(spec) {
  const errors = [];
  if (!spec || typeof spec !== "object") errors.push("Spec inválido.");
  if (spec?.schemaVersion !== 1) errors.push("schemaVersion debe ser 1.");
  if (!ALLOWED_FAMILIES.has(spec?.family)) errors.push(`Familia no permitida: ${spec?.family || "-"}.`);
  if (!ALLOWED_CHART_TYPES.has(spec?.chartType)) errors.push(`Tipo no permitido: ${spec?.chartType || "-"}.`);
  (Array.isArray(spec?.metrics) ? spec.metrics : []).forEach((metric) => {
    if (!METRIC_SET.has(metric)) errors.push(`Métrica no permitida: ${metric}.`);
  });
  if (spec?.chartType === "doughnut" && !["metric_by_entity", "derived_metric_by_entity", "ranking"].includes(spec?.family)) {
    errors.push("Doughnut solo se permite para composición por entidad o ranking.");
  }
  if (spec?.family === "correlation" && (!METRIC_SET.has(spec.metricX) || !METRIC_SET.has(spec.metricY))) {
    errors.push("Correlación requiere metricX y metricY válidas.");
  }
  return { ok: errors.length === 0, errors };
}

function toNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function round(value) {
  return Number.isFinite(value) ? Math.round(value * 100) / 100 : null;
}

function safeDivide(numerator, denominator) {
  const n = toNumber(numerator);
  const d = toNumber(denominator);
  if (n === null || d === null || d === 0) return null;
  return round(n / d);
}

function getFeatureDefinition(spec, featureId) {
  const fromSpec = (Array.isArray(spec?.derivedFeatures) ? spec.derivedFeatures : []).find(
    (feature) => feature?.id === featureId
  );
  return fromSpec || DERIVED_FEATURES[featureId] || null;
}

function computeFeatureFromMetrics(metrics, feature, previousMetrics = null) {
  if (!feature) return null;
  if (feature.operation === "ratio") {
    return safeDivide(metrics?.[feature.numeratorMetric], metrics?.[feature.denominatorMetric]);
  }
  if (feature.operation === "delta") {
    const current = toNumber(metrics?.[feature.sourceMetric]);
    const previous = toNumber(previousMetrics?.[feature.sourceMetric]);
    return current !== null && previous !== null ? round(current - previous) : null;
  }
  if (feature.operation === "movingAverage") {
    return toNumber(metrics?.[feature.sourceMetric]);
  }
  return null;
}

function getEntityTable(scappContext) {
  return scappContext?.digest?.entityTable || [];
}

function getDailyRollup(scappContext) {
  return scappContext?.digest?.dailyRollup || [];
}

function getEntityDailyPayload(scappContext) {
  return scappContext?.blocks?.["entity-daily-series-main"]?.payload || {};
}

function filterEntities(rows, spec) {
  const names = spec?.filters?.entityNames || spec?.filters?.entities || [];
  if (!Array.isArray(names) || !names.length) return rows;
  const wanted = new Set(names.map((name) => String(name).toLowerCase()));
  return rows.filter((row) => wanted.has(String(row.name || row.entityId || "").toLowerCase()));
}

function datasetColor(index) {
  const colors = [
    "rgba(232, 78, 14, 0.82)",
    "rgba(96, 165, 250, 0.82)",
    "rgba(74, 222, 128, 0.82)",
    "rgba(251, 191, 36, 0.82)",
    "rgba(205, 126, 206, 0.82)",
    "rgba(248, 113, 113, 0.82)",
  ];
  return colors[index % colors.length];
}

function buildMetricByEntity(spec, scappContext) {
  const rows = filterEntities(getEntityTable(scappContext), spec);
  const metric = spec.metrics?.[0] || spec.y?.[0]?.metric || "alarms_count";
  const featureId = spec.y?.[0]?.feature || spec.derivedFeatures?.[0]?.id;
  const feature = getFeatureDefinition(spec, featureId);
  const values = rows.map((row) =>
    feature ? computeFeatureFromMetrics(row.metrics, feature) : toNumber(row.metrics?.[metric])
  );
  let pairs = rows.map((row, index) => ({ label: row.name, value: values[index] }));
  if (spec.family === "ranking") {
    const direction = JSON.stringify(spec.transforms || []).includes("rankBottom") ? 1 : -1;
    pairs = pairs
      .filter((item) => item.value !== null)
      .sort((a, b) => direction * (a.value - b.value))
      .slice(0, spec.filters?.limit || 8);
  }
  return {
    labels: pairs.map((item) => item.label),
    datasets: [
      {
        label: feature?.label || metric,
        data: pairs.map((item) => item.value),
        backgroundColor:
          spec.chartType === "doughnut" ? pairs.map((_, index) => datasetColor(index)) : datasetColor(0),
        borderColor: datasetColor(0),
        borderWidth: 1,
      },
    ],
  };
}

function buildMetricByTime(spec, scappContext) {
  const rollup = getDailyRollup(scappContext);
  const metric = spec.metrics?.[0] || spec.y?.[0]?.metric || "alarms_count";
  const featureId = spec.y?.[0]?.feature || spec.derivedFeatures?.[0]?.id;
  const feature = getFeatureDefinition(spec, featureId);
  const labels = rollup.map((row) => row.day);
  let previous = null;
  const data = rollup.map((row) => {
    const value = feature ? computeFeatureFromMetrics(row.value, feature, previous) : toNumber(row.value?.[metric]);
    previous = row.value;
    return value;
  });
  return {
    labels,
    datasets: [
      {
        label: feature?.label || metric,
        data,
        borderColor: datasetColor(0),
        backgroundColor: "rgba(232, 78, 14, 0.18)",
        tension: 0.25,
        fill: spec.chartType === "line",
      },
    ],
  };
}

function pearson(points) {
  if (points.length < 3) return null;
  const n = points.length;
  const sumX = points.reduce((acc, point) => acc + point.x, 0);
  const sumY = points.reduce((acc, point) => acc + point.y, 0);
  const sumXY = points.reduce((acc, point) => acc + point.x * point.y, 0);
  const sumX2 = points.reduce((acc, point) => acc + point.x * point.x, 0);
  const sumY2 = points.reduce((acc, point) => acc + point.y * point.y, 0);
  const denominator = Math.sqrt((n * sumX2 - sumX ** 2) * (n * sumY2 - sumY ** 2));
  if (!denominator) return null;
  return round((n * sumXY - sumX * sumY) / denominator);
}

function buildMetricVsMetric(spec, scappContext) {
  const payload = getEntityDailyPayload(scappContext);
  const metricX = spec.metricX || spec.metrics?.[0] || "sfr_value";
  const metricY = spec.metricY || spec.metrics?.[1] || "delivered_food";
  const entities = filterEntities(payload.series || [], spec);
  const groupedDatasets = entities.map((entity, entityIndex) => {
    const xValues = entity.values?.[metricX] || [];
    const yValues = entity.values?.[metricY] || [];
    const points = [];
    xValues.forEach((xValue, index) => {
      const x = toNumber(xValue);
      const y = toNumber(yValues[index]);
      if (x !== null && y !== null) points.push({ x, y, entity: entity.name, day: payload.days?.[index] });
    });
    const r = pearson(points);
    return {
      label: r === null ? entity.name : `${entity.name} (r=${r})`,
      data: points,
      backgroundColor: datasetColor(entityIndex),
      parsing: { xAxisKey: "x", yAxisKey: "y" },
    };
  });

  const shouldGroupByEntity =
    spec.family === "correlation" ||
    spec.filters?.groupBy === "entity" ||
    spec.filters?.groupBy === "jaula";
  if (shouldGroupByEntity && groupedDatasets.length > 1) {
    return { datasets: groupedDatasets.filter((dataset) => dataset.data.length) };
  }

  const points = groupedDatasets.flatMap((dataset) => dataset.data);
  const r = pearson(points);
  return {
    datasets: [
      {
        label: r === null ? `${metricX} vs ${metricY}` : `${metricX} vs ${metricY} (r=${r})`,
        data: points,
        backgroundColor: datasetColor(0),
        parsing: { xAxisKey: "x", yAxisKey: "y" },
      },
    ],
  };
}

export function buildInsightChartData(spec, scappContext) {
  if (["metric_by_entity", "derived_metric_by_entity", "ranking"].includes(spec.family)) {
    return buildMetricByEntity(spec, scappContext);
  }
  if (["metric_by_time", "derived_metric_by_time"].includes(spec.family)) {
    return buildMetricByTime(spec, scappContext);
  }
  if (["metric_vs_metric", "correlation"].includes(spec.family)) {
    return buildMetricVsMetric(spec, scappContext);
  }
  return { labels: [], datasets: [] };
}
